import styles from './project-memory-book-actions.module.less';
import { useState } from 'react';
import {
    WRITE_MEMORIAL_NOTE_TEXT_KEY,
    SOMETHING_WENT_WRONG_ERROR_TEXT_KEY,
    MEMORIAL_NOTE_SUBMITTED_SUCCESS_TEXT_KEY,
    MEMORIAL_NOTE_WILL_BE_SHOWN_TEXT_KEY,
    MEMORIAL_NOTE_PLACEHOLDER_TEXT_KEY,
    YOUR_NAME_TEXT_KEY,
    EMAIL_TEXT_KEY,
    MEMORIAL_NOTE_TEXT_KEY,
    SEND_TEXT_KEY,
    MEMORIAL_NOTE_SENT_TEXT_KEY,
    OPTIONAL_TEXT_KEY,
} from '../i18n/project-i18n-model';
import { useI18nTexts } from '../i18n/useI18nTexts';
import { isValidEmail, emailErrorMessage, isValidName, getNameErrorMessage } from '../../user/user-model';
import { Input, InputGroup, TextArea } from '@sb1/ffe-form-react';
import { ContextMessage } from '@sb1/ffe-messages-react';
import { PrimaryButton } from '@sb1/ffe-buttons-react';
import { useMemoryBookAction } from './useMemoryBookAction';
import MemoryBookActionPage from './MemoryBookActionPage';
import isLength from 'validator/lib/isLength';
import { fetchProjectCard, fetchProjectTransactionAggregates, postCommentWithoutTransaction } from '../project-actions';
import { Heading2 } from '@sb1/ffe-core-react';
import { trackEvent } from '../../support-and-tracking/analytics-actions';
import { NameAndCommentImage } from '../payment/name-and-comment/NameAndCommentImage';
import ProjectContext from '../project-context';
import { getProjectFromId } from '../projects-reducer';

const isValidMessage = (message) => message && isLength(message, { min: 1, max: 200 });
const messageErrorMessage = () => 'Minneordet kan ikke være lengre enn 200 tegn';

const validateForm = ({ name, email, message }) => ({
    nameError: name && !isValidName(name) ? getNameErrorMessage() : '',
    emailError: isValidEmail(email) ? '' : emailErrorMessage(),
    messageError: message && !isValidMessage(message) ? messageErrorMessage() : '',
});

const ProjectWriteMemorialNote = () => {
    const {
        project,
        dispatch,
        isSubmitted,
        setIsSubmitted,
        isLoading,
        setIsLoading,
        error,
        setError,
        dismissSupport,
        navigateToPayment,
        defaultValues,
    } = useMemoryBookAction('memorial-note');
    const [name, setName] = useState(defaultValues.name);
    const [email, setEmail] = useState(defaultValues.email);
    const [commentImageUrl, setCommentImageUrl] = useState('');
    const [message, setMessage] = useState('');
    const [nameError, setNameError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [messageError, setMessageError] = useState('');
    const [submittedCommentId, setSubmittedCommentId] = useState(null);
    const texts = useI18nTexts([
        SOMETHING_WENT_WRONG_ERROR_TEXT_KEY,
        MEMORIAL_NOTE_SUBMITTED_SUCCESS_TEXT_KEY,
        MEMORIAL_NOTE_WILL_BE_SHOWN_TEXT_KEY,
        MEMORIAL_NOTE_PLACEHOLDER_TEXT_KEY,
        YOUR_NAME_TEXT_KEY,
        EMAIL_TEXT_KEY,
        MEMORIAL_NOTE_TEXT_KEY,
        SEND_TEXT_KEY,
        WRITE_MEMORIAL_NOTE_TEXT_KEY,
        MEMORIAL_NOTE_SENT_TEXT_KEY,
        OPTIONAL_TEXT_KEY,
    ]);

    const onSubmit = (e) => {
        e.preventDefault();
        const {
            nameError: nameErr,
            emailError: emailErr,
            messageError: msgErr,
        } = validateForm({ name, email, message, commentImageUrl });
        setNameError(nameErr);
        setEmailError(emailErr);
        setMessageError(msgErr);
        if (nameErr || emailErr || msgErr) {
            return;
        }
        if (!message && !commentImageUrl) {
            setError('Du må sende med et minneord eller laste opp et bilde');
            return;
        }
        setIsLoading(true);
        dispatch(
            postCommentWithoutTransaction({
                projectId: project.id,
                authorName: name,
                email,
                comment: message,
                commentImageUrl: commentImageUrl?.length > 0 ? commentImageUrl : undefined,
            })
        )
            .then((comment) => {
                setIsSubmitted(true);
                setSubmittedCommentId(comment?.body?.id);
                window.scrollTo(0, 0);
                trackEvent('project-write-memorial-note', 'success', { projectId: project.id });
            })
            .catch(() => setError(texts[SOMETHING_WENT_WRONG_ERROR_TEXT_KEY]))
            .finally(() => setIsLoading(false));
    };

    return (
        <ProjectContext.Provider value={project}>
            <MemoryBookActionPage
                project={project}
                headingText={
                    isSubmitted
                        ? `${texts[MEMORIAL_NOTE_SENT_TEXT_KEY]} ✍️`
                        : `${texts[WRITE_MEMORIAL_NOTE_TEXT_KEY]} ✍️`
                }
                isLoading={isLoading}
                isSubmitted={isSubmitted}
                successMessage={texts[MEMORIAL_NOTE_SUBMITTED_SUCCESS_TEXT_KEY]}
                onDismiss={dismissSupport}
                onNavigateToPayment={() =>
                    navigateToPayment({
                        memorialCommentId: submittedCommentId,
                        authorName: name,
                        memorialNote: message,
                    })
                }
            >
                <Heading2 lookLike={6}>{texts[WRITE_MEMORIAL_NOTE_TEXT_KEY]}</Heading2>
                <p>{texts[MEMORIAL_NOTE_WILL_BE_SHOWN_TEXT_KEY]}</p>
                <form onSubmit={onSubmit} className={styles.formContainer}>
                    <div>
                        <InputGroup
                            label={`${texts[YOUR_NAME_TEXT_KEY]} ${texts[OPTIONAL_TEXT_KEY]}`}
                            fieldMessage={nameError || undefined}
                            extraMargin={false}
                        >
                            <Input
                                value={name}
                                maxLength={50}
                                onChange={(e) => setName(e.target.value)}
                                onBlur={() => setNameError(name && !isValidName(name) ? getNameErrorMessage() : '')}
                            />
                        </InputGroup>
                    </div>
                    <div>
                        <InputGroup
                            label={texts[EMAIL_TEXT_KEY]}
                            fieldMessage={emailError || undefined}
                            extraMargin={false}
                        >
                            <Input
                                value={email}
                                maxLength={50}
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={() => setEmailError(isValidEmail(email) ? '' : emailErrorMessage())}
                            />
                        </InputGroup>
                    </div>
                    <div>
                        <InputGroup
                            label={`${texts[MEMORIAL_NOTE_TEXT_KEY]} ${texts[OPTIONAL_TEXT_KEY]}`}
                            fieldMessage={messageError || undefined}
                            extraMargin={false}
                        >
                            <TextArea
                                value={message}
                                maxLength={200}
                                placeholder={texts[MEMORIAL_NOTE_PLACEHOLDER_TEXT_KEY]}
                                onChange={(e) => setMessage(e.target.value)}
                                onBlur={() =>
                                    setMessageError(message && !isValidMessage(message) ? messageErrorMessage() : '')
                                }
                            />
                        </InputGroup>
                    </div>
                    <div>
                        <div className={styles.uploadButton}>
                            <NameAndCommentImage imageUrl={commentImageUrl} setImageUrl={setCommentImageUrl} />
                        </div>
                    </div>
                    {error && <ContextMessage type="error">{error}</ContextMessage>}
                    <div>
                        <PrimaryButton type="submit" isLoading={isLoading}>
                            {texts[SEND_TEXT_KEY]}
                        </PrimaryButton>
                    </div>
                </form>
            </MemoryBookActionPage>
        </ProjectContext.Provider>
    );
};

export const loader = async ({ getState, dispatch, params }) => {
    const { projectId } = params;
    await dispatch(fetchProjectCard(projectId));
    await dispatch(fetchProjectTransactionAggregates(projectId));

    const state = getState();
    const project = getProjectFromId(state, projectId);
    if (!project?.created_at) {
        throw new Response(null, { status: 404 });
    }
};

export default ProjectWriteMemorialNote;
